// Wire-format types for the login flow (PRD 00).
// Contract: docs/api-contracts/login.md.

export type AccountType = 'ADMIN' | 'MEDICAL_STAFF';

export interface LoginInput {
  email: string;
  password: string;
}

export interface LoginUser {
  id: number;
  email: string;
  name: string;
  accountType: AccountType;
  // 0..N non-root roles (PRD 15 §4.1). Root returns [] + isRoot=true.
  roles: string[];
  isRoot: boolean;
  // PRD 00 §3.3.1 — temporary password issued by an admin (PRD 15 §3.6) ⇒ forced reset.
  mustChangePassword: boolean;
  // Fast-hydration profile fields (PRD 20 §13.1) — so the GNB and My Profile
  // can render immediately without waiting for GET /auth/me.
  phone: string | null;
  institution: string;
  joinedAt: string;
  passwordChangedAt: string | null;
}

// Login itself is handled by api/auth.ts (authenticate() → Firebase exchange → /me); its result/error
// types live there (AuthenticateResult / LoginError). LoginUser is retained only for the dev mock seeds.

// ── Reset-password flow (PRD 00 §3.3) ────────────────────────────────

// POST /admin/auth/password-reset/request-otp — body { email }.
// Per PRD 00 §3.3 / api-contracts/login.md §2.3: an unregistered email is rejected with
// EMAIL_NOT_REGISTERED ("No registered account found."); the FE keeps the user on step 1.
export interface SendResetCodeInput {
  email: string;
}

// POST /admin/auth/password-reset/verify-otp — body { email, otp } → 200 { data: { resetToken } }.
// The FE collects the 6-digit value as `code`; the adapter maps it to the BE field `otp`.
export interface VerifyResetCodeInput {
  email: string;
  code: string;
}

export interface VerifyResetCodeResult {
  resetToken: string;
}

// POST /admin/auth/password-reset/complete (forgot-password mode) → 204 no body.
export interface ChangePasswordInput {
  resetToken: string;
  newPassword: string;
  confirmPassword: string;
}

// POST /admin/auth/password-reset/complete (forced post-login mode, { forced: true }) → 204 no body.
export interface ForcedChangePasswordInput {
  newPassword: string;
  confirmPassword: string;
}

export type ResetApiErrorCode =
  | 'EMAIL_INVALID'
  | 'EMAIL_NOT_REGISTERED'
  | 'EMAIL_SEND_FAILED'
  | 'OTP_INVALID'
  | 'OTP_EXPIRED'
  | 'OTP_RATE_LIMITED'
  | 'RESET_TOKEN_INVALID'
  | 'PASSWORD_POLICY_VIOLATION'
  | 'PASSWORD_REUSED'
  | 'PASSWORD_CONFIRM_MISMATCH'
  | 'INTERNAL_ERROR'
  | 'UNKNOWN';

export class ResetApiError extends Error {
  readonly code: ResetApiErrorCode;
  readonly fields?: Record<string, string>;

  constructor(
    code: ResetApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string> },
  ) {
    super(message);
    this.name = 'ResetApiError';
    this.code = code;
    this.fields = options?.fields;
    Object.setPrototypeOf(this, ResetApiError.prototype);
  }
}

export interface LoginApi {
  sendResetCode(input: SendResetCodeInput): Promise<void>;
  verifyResetCode(input: VerifyResetCodeInput): Promise<VerifyResetCodeResult>;
  changePassword(input: ChangePasswordInput): Promise<void>;
  forcedChangePassword(input: ForcedChangePasswordInput): Promise<void>;
}
